import { ArrowRight, type LucideIcon } from "lucide-react";
import Image from "next/image";

import { ctaButtonClassName } from "@components/bluetooth-landing/ctaButtonStyles";
import { useHeroAnimatedBackground } from "@components/bluetooth-landing/HeroAnimatedBackground";

export interface HeroCopy {
  header: string;
  headerTail: string;
  description: string;
  cta: {
    text: string;
    href: string;
  };
  secondaryCta: {
    text: string;
    href: string;
  };
  benefits: {
    text: string;
    Icon: LucideIcon;
  }[];
}

export function Hero({ copy }: { copy: HeroCopy }) {
  const { header, headerTail, description, cta, secondaryCta, benefits } = copy;
  const { sectionProps, background } = useHeroAnimatedBackground();

  return (
    <section
      className="relative bg-background text-white overflow-hidden pt-24 pb-32"
      {...sectionProps}
    >
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 opacity-20">
          <Image
            src="/bluetooth-landing/hero-bluetooth.jpg"
            alt="Modern mobile app bluetooth technology"
            fill
            priority
            className="object-cover"
            sizes="100vw"
          />
        </div>
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-background via-background/90 to-transparent" />
        <div className="pointer-events-none absolute inset-0 [mask-image:linear-gradient(to_right,rgba(0,0,0,0.70)_0%,rgba(0,0,0,0.35)_35%,transparent_60%)] [-webkit-mask-image:linear-gradient(to_right,transparent_0%,rgba(0,0,0,0.35)_35%,black_60%)]">
          {background}
        </div>
      </div>

      <div className="container mx-auto px-6 md:px-12 relative z-10 max-w-7xl">
        <div className="max-w-3xl mt-16">
          <h1 className="text-4xl md:text-5xl lg:text-7xl/normal font-medium mb-6">
            {header}{" "}
            <span className="font-semibold text-lime-100">{headerTail}</span>
          </h1>
          <p className="text-xl md:text-2xl text-slate-300 mb-8 max-w-2xl">
            {description}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 mb-10">
            <a
              href={cta.href}
              className={ctaButtonClassName(
                "inline-flex justify-center items-center gap-2 font-medium py-4 px-8 rounded-full text-lg",
              )}
            >
              {cta.text}
              <ArrowRight size={20} />
            </a>
            <a
              href={secondaryCta.href}
              className="inline-flex justify-center items-center gap-2 bg-surface-dark hover:bg-surface-dark/90 text-white font-medium py-4 px-8 rounded-full transition-colors text-lg border border-slate-700"
            >
              {secondaryCta.text}
            </a>
          </div>

          <div className="flex flex-col sm:flex-row gap-6 text-sm text-slate-400">
            {benefits.map(benefit => (
              <div key={benefit.text} className="flex items-center gap-2">
                <benefit.Icon size={16} className="text-green-600" />
                <span>{benefit.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
